import { IRecognitionException } from 'chevrotain';
import { SrsParser } from 'millan';
import { RDFS_IRI, RDF_IRI, RDF_TYPE_IRI, XSD_IRI } from 'vet-bones/constants';

import { OWL_IRI, STARDOG_API_IRI } from 'src/templates/queries/iris';

/*
TODO: this utility is very incomplete!!! Known issues:
- No support for parsing bnodes
- Currently ignores any ^^xsd: type info
- Currently ignores any SRS rules (but shouldn't crash if it encounters them)
- Edge properties are ignored
*/

const srsParser = new SrsParser();

export interface Triple {
  subject: string;
  predicate: string;
  object: string;
}

type CstLeafNode = {
  image: string;
  tokenType: { name: string };
};

type CstNode = {
  name: string;
  children: Record<string, Array<CstNode | CstLeafNode>>;
};

type CstParseResults = {
  errors: IRecognitionException[];
  semanticErrors: IRecognitionException[];
  triples: Triple[];
  prefixes: Record<string, string>;
};

const BLANK_NODE_PLACEHOLDER = 'BLANK_NODE_PLACEHOLDER';

/*
  We manually patch a set of standard prefixes because
  millan does not report these as semanticErrors when they are missing;
  see millan/src/turtle/defaultNamespaces.ts
*/
const STANDARD_PREFIXES: Record<string, string> = {
  '': '',
  rdf: RDF_IRI,
  rdfs: RDFS_IRI,
  xsd: XSD_IRI,
  owl: OWL_IRI,
  stardog: STARDOG_API_IRI,
};

const extractLiteral = (node: CstNode) =>
  (
    (node.children.STRING_LITERAL_QUOTE?.[0] ||
      node.children.STRING_LITERAL_LONG_QUOTE?.[0]) as CstLeafNode
  ).image.replace(/^"*|"*$/g, '');

const getIris = (
  nodes: Array<CstNode | CstLeafNode>,
  parseResults: CstParseResults
): string[] => {
  return nodes.reduce((agg: string[], node) => {
    if ((node as CstLeafNode).image === 'a') {
      if (!parseResults.prefixes.rdf) {
        // eslint-disable-next-line no-param-reassign
        parseResults.prefixes.rdf = STANDARD_PREFIXES.rdf;
      }
      return [...agg, RDF_TYPE_IRI];
    }
    if ((node as CstNode).name === 'String') {
      return [...agg, extractLiteral(node as CstNode)];
    }
    if ((node as CstNode).name === 'PrefixedName') {
      const matchResults = (
        (node as CstNode).children.PNAME_LN[0] as CstLeafNode
      ).image.match(/([^:]*):(.*)/);
      if (matchResults === null) {
        throw new Error(`PrefixedName does not contain expected : character`);
      }
      const prefix = matchResults[1];
      const value = matchResults[2];
      if (!(prefix in parseResults.prefixes)) {
        const lowerPrefix = prefix.toLocaleLowerCase();
        if (lowerPrefix in STANDARD_PREFIXES) {
          // eslint-disable-next-line no-param-reassign
          parseResults.prefixes[prefix] = STANDARD_PREFIXES[lowerPrefix];
        } else {
          throw new Error(`Could not infer missing prefix: ${prefix}`);
        }
      }
      return [...agg, `${parseResults.prefixes[prefix]}${value}`];
    }
    if ((node as CstLeafNode).tokenType?.name === 'IRIREF') {
      return [...agg, (node as CstLeafNode).image.replace(/^\s*<|>\s*$/g, '')];
    }
    if ((node as CstNode).name === 'RDFLiteral') {
      // TODO: This ignores xsd type info
      return [
        ...agg,
        extractLiteral((node as CstNode).children.String[0] as CstNode),
      ];
    }
    if ((node as CstNode).name === 'blankNodePropertyList') {
      return [...agg, BLANK_NODE_PLACEHOLDER];
    }
    // TODO: Deal with other CstNode.name types...
    return Object.values((node as CstNode).children || []).reduce(
      (agg2: string[], nodeList: Array<CstNode | CstLeafNode>) => [
        ...agg2,
        ...getIris(nodeList, parseResults),
      ],
      agg
    );
  }, []);
};

const handleTurtleCstNode = (
  node: CstNode,
  existingResults: CstParseResults
): CstParseResults => {
  let pname: string;
  let iri: string;
  let newTriples: Triple[];
  let subjectIris: string[];
  switch (node.name) {
    case 'sparqlPrefix':
    case 'prefixID':
      pname = (node.children.PNAME_NS[0] as CstLeafNode).image.replace(
        /[:\s]*$/,
        ''
      );
      iri = (node.children.IRIREF[0] as CstLeafNode).image.replace(
        /^[<\s]*|[>\s]*$/g,
        ''
      );
      return {
        ...existingResults,
        prefixes: {
          ...existingResults.prefixes,
          [pname]: iri,
        },
      };
    case 'triples':
      // Ignore any triples that involve edge properties
      // (if they exist, don't return anything, and instead proceed
      // to switch { default: })
      if (!node.children.EmbeddedTriplePattern) {
        newTriples = [];
        subjectIris = getIris(
          node.children.subject as CstNode[],
          existingResults
        );
        node.children.predicateObjectList.forEach(
          (poList: CstNode | CstLeafNode) => {
            const verbIris = getIris(
              (poList as CstNode).children.verb as CstNode[],
              existingResults
            );
            const objectIris = (
              (poList as CstNode).children.objectList as CstNode[]
            ).map((objectList) =>
              getIris(objectList.children.object as CstNode[], existingResults)
            );
            // TODO: I feel like zipping subjects / verbs / objects this way is
            // probably wrong in general...? There might be some form of turtle
            // that could break this
            subjectIris.forEach((subject) => {
              verbIris.forEach((predicate, index) => {
                objectIris[index].forEach((object) => {
                  // Ignore blank nodes for now
                  if (object !== BLANK_NODE_PLACEHOLDER) {
                    newTriples.push({
                      subject,
                      predicate,
                      object,
                    });
                  }
                });
              });
            });
          }
        );
        return {
          ...existingResults,
          triples: [...existingResults.triples, ...newTriples],
        };
      }
    default:
      return Object.values(node.children || []).reduce(
        (agg: CstParseResults, nodeList: Array<CstNode | CstLeafNode>) => {
          let tempResults = {
            errors: agg.errors,
            semanticErrors: agg.errors,
            triples: agg.triples,
            prefixes: agg.prefixes,
          };
          nodeList.forEach((childNode) => {
            tempResults = handleTurtleCstNode(
              childNode as CstNode,
              tempResults
            );
          });
          return tempResults;
        },
        existingResults
      );
  }
};

export const parseTurtleTriples = (rawTurtle: string) => {
  const { errors, semanticErrors, cst } = srsParser.parse(rawTurtle, 'stardog');
  const initialResults: CstParseResults = {
    errors,
    semanticErrors,
    triples: [],
    prefixes: {},
  };
  if (errors.length || semanticErrors.length) {
    return initialResults;
  }
  // TODO: SRS rules are ignored for now
  return handleTurtleCstNode(
    cst?.children?.turtleDoc?.[0] as CstNode,
    initialResults
  );
};
